import { useTranslation } from "react-i18next";
import MUIDataTable, { MUIDataTableColumnDef } from "mui-datatables";
import { LiveTrainingData } from "models/LiveTrainingData";
import { useLiveTrainingInfo } from "hooks/useLiveTrainingInfo";
import { CircularProgress, styled, TableBody, TableCell, Typography, useTheme } from "@mui/material";
import TimeDifference from "components/TimeDifference";
import { useEffect, useState } from "react";
import { LessonProgressStatus } from "models/LessonProgressStatus";
import { createColorFilteredComponent } from "helpers/styledHelpers";
import LiveDataTableRow from "./LiveDataTableRow/LiveDataTableRow";
import { NoDataRow } from "./LiveDataTableRow/NoDataRow";
import { MakeSortableHead } from "helpers/sortTableHeaderHelper";

const StyledAnswerSpan = createColorFilteredComponent<{ $color: string }>('span')(({ $color }) => ({
  color: $color
}));

const StyledPointsSpan = createColorFilteredComponent<{ $color: string }>('span')(({ $color }) => ({
  color: $color
}));

const StyledTableCell = styled("td")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

interface LiveDataTableProps {
    userIds: string[];
    lessonIds: string [];
    onDataDelete: (removedIds: string[]) => void;
}

export const LiveDataTable = (props: LiveDataTableProps) => {
    const { t } = useTranslation();
    const { liveTrainingData, loading } = useLiveTrainingInfo(props.userIds, props.lessonIds);
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    const [rows, setRows] = useState(liveTrainingData);

    const theme = useTheme();

    const handleRowExpand = (currentRowsExpanded: any[], allRowsExpanded: any[]) => {
        setExpandedRows(allRowsExpanded.map(row => row.index));
    }

    useEffect(() => {
        setRows(liveTrainingData);
    }, [liveTrainingData])

    const handleRowsDelete = (rowsDeleted: any, newTableData: any) => {
        const idsToDelete = rowsDeleted.data.map((d: any)=> rows[d.dataIndex].userId);
        setRows(prevRows => prevRows.filter(row => !idsToDelete.includes(row.userId)));
        props.onDataDelete(idsToDelete);
    }

    const getLessonProgressStatusText = (status: LessonProgressStatus) => {
        switch (status) {
            case LessonProgressStatus.NotStarted:
                return t('liveData.status.notStarted');
            case LessonProgressStatus.Finished:
                return t('liveData.status.finished');
            case LessonProgressStatus.Canceled:
                return t('liveData.status.canceled');
            default:
                return '';
        }
    };

    const rowFontSize = 14;

    const customBodyRenderOptions = (value: string) => {
        return (
            <Typography textAlign="left" fontSize={rowFontSize} ml="0.4rem">
                {value}
            </Typography>
        )
    }

    const columns: MUIDataTableColumnDef[] = [
        { name: "userId", label: "Id", options: { filter: false, sort: false, display: 'excluded' } },
        { name: "sequenceId", options: { filter: false, sort: false, display: 'excluded' } },
        { name: "sequenceName", options: { filter: false, sort: false, display: 'excluded' } },
        { 
            name: "userDisplayName", 
            label: t('liveData.name'),
            options: {
                customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
                        return (
                            <TableCell key={columnMeta.index} align="left">
                              <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                                 {columnMeta.label}
                              </MakeSortableHead>
                            </TableCell>
                        );
                },                
                customBodyRender: (value: any) => {
                    return (
                        customBodyRenderOptions(value)
                    )
                }
            } 
        },
        { 
            name: "lessonName", 
            label: t('liveData.lesson'),
            options: {
                customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
                        return (
                            <TableCell key={columnMeta.index} align="left">
                              <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                                 {columnMeta.label}
                              </MakeSortableHead>
                            </TableCell>
                        );
                },   
                customBodyRender: (value: any) => {
                    return (
                        customBodyRenderOptions(value)
                    )
                }
            } 
        },       
        { name: "answer", label: t('liveData.lastInteraction'),
        options: {
            filter: false,
            sort: false,
            customHeadRender: ({index, ...column}) => {
                return (
                    <TableCell key={index} align="left">
                        {column.label}
                    </TableCell>
                )
            },
            customBodyRender: (_: any, tableMeta: any, __: any) => {
                const liveTrainingDataForRow: LiveTrainingData = rows[tableMeta.rowIndex];

                if (!rows || !liveTrainingDataForRow) {
                    return null;
                }

                const lastAnswer = liveTrainingDataForRow.answers?.[liveTrainingDataForRow.selectedAnswerIndex ?? 0];
                return !lastAnswer
                    ? <Typography color={theme.palette.error.main} fontSize={rowFontSize} ml="0.4rem">
                        {t('liveData.noAnswer')}
                    </Typography>
                    : <Typography fontSize={rowFontSize}>
                        <StyledAnswerSpan $color={lastAnswer.answerPoints > 0 ? theme.palette.secondary.main : theme.palette.error.main}>
                            {`${lastAnswer.answerIndex + 1}. ${lastAnswer.answerText}`}
                        </StyledAnswerSpan>
                        <br/>
                        <StyledAnswerSpan $color={lastAnswer.answerPoints > 0 ? theme.palette.secondary.main : theme.palette.error.main}>
                            {`${lastAnswer.answerPoints}/`}
                        </StyledAnswerSpan>
                        <StyledPointsSpan $color={theme.palette.secondary.main}>
                            {!liveTrainingDataForRow.answers ? 0 : Math.max(...liveTrainingDataForRow.answers?.map(a => a.answerPoints))}
                        </StyledPointsSpan>
                    </Typography>
                    
            }
        } },
        { name: "pointsSum", label: t('liveData.points'),
            options: {
                customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
                        return (
                            <TableCell key={columnMeta.index} align="center">
                              <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                                 {columnMeta.label}
                              </MakeSortableHead>
                            </TableCell>
                        );
                },   
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return <Typography textAlign={'center'} fontSize={rowFontSize}>
                            {value || 0}
                        </Typography>
                }
        } },
        { name: "lessonStartedAt", label: t('liveData.time'),
            options: {
                filter: false,
                sort: false,
                customHeadRender: ({index, ...column}) => {
                    return (
                        <TableCell key={index} align="center">
                            {column.label}
                        </TableCell>
                    )
                },
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    const rowData = rows[tableMeta.rowIndex];
                    const status = rowData.status;
                    if (status === LessonProgressStatus.InProgress) {
                        return (
                            <Typography textAlign={'center'} fontSize={rowFontSize}>
                                <TimeDifference dateFrom={new Date(rowData.lessonStartedAt)} dateTo={!rowData.lessonCompletedAt ? null : new Date(rowData.lessonCompletedAt)}/>
                            </Typography>
                        );
                    }

                    const isCanceled = status === LessonProgressStatus.Canceled;
                    return (
                        <Typography textAlign={'left'} fontSize={rowFontSize}
                                    color={isCanceled ? theme.palette.error.main : "inherit"}>
                            {getLessonProgressStatusText(status)}
                        </Typography>
                    );
                }
        } },
    ];

    return (
        (loading && rows.length === 0)
        ? <CircularProgress />
        : <MUIDataTable
            title={t('liveData.title')}
            data={rows}
            columns={columns}
            options={{
                textLabels: {
                    body: {
                      noMatch: t('dataTable.noMatch'),
                      toolTip: t('dataTable.toolTip'),
                      columnHeaderTooltip: column => t('dataTable.columnHeaderTooltip', { column: column.label })
                    },
                    pagination: {
                      next: t('dataTable.next'),
                      previous: t('dataTable.previous'),
                      rowsPerPage: t('dataTable.rowsPerPage'),
                      displayRows: t('dataTable.displayRows'),
                    },
                    toolbar: {
                      search: t('dataTable.search'),
                      downloadCsv: t('dataTable.downloadCsv'),
                      print: t('dataTable.print'),
                      viewColumns: t('dataTable.viewColumns'),
                      filterTable: t('dataTable.filterTable'),
                    },
                    filter: {
                      all: t('dataTable.all'),
                      title: t('dataTable.title'),
                      reset: t('dataTable.reset'),
                    },
                    viewColumns: {
                      title: t('dataTable.viewColumnsTitle'),
                      titleAria: t('dataTable.viewColumnsTitleAria'),
                    },
                    selectedRows: {
                      text: t('dataTable.selectedRowsText'),
                      delete: t('dataTable.delete'),
                      deleteAria: t('dataTable.deleteAria'),
                    },
                },
                fixedHeader: false,
                responsive: 'standard',
                expandableRows: true,
                onRowsDelete: handleRowsDelete,
                renderExpandableRow: (rowData, rowMeta) => {
                    const row = rows[rowMeta.dataIndex];

                    if(!row.lessonStartedAt){
                        const colSpan = rowData.length + 2;

                        return (
                            <tr>
                                <td colSpan={colSpan}>
                                    <NoDataRow />
                                </td>
                            </tr>
                            );
                    }

                    const colSpan = rowData.length + 1;

                    return (
                        <tr>
                            <StyledTableCell colSpan={colSpan}>
                                <TableBody>                                 
                                    <LiveDataTableRow
                                    question={row.question}
                                    answers={row.answers ?? []}
                                    selectedAnswerIndex={row.selectedAnswerIndex}
                                    answerTime={row.answerTime}
                                    nodeType={row.nodeType}
                                    />
                                </TableBody>
                            </StyledTableCell>
                        </tr>
                )},
                rowsExpanded: expandedRows,
                onRowExpansionChange: handleRowExpand
            }}
        />
    );
};